import { useEffect, useState } from 'react';

interface AnimatedItemProps {
  /** Render prop (not plain children) so the caller controls the actual markup — this only hands
   * back inline styles to apply, it never wraps the caller's element (e.g. a `<tr>`) in an extra
   * DOM node that would break that element's structure. */
  children: (styles: React.CSSProperties) => React.ReactElement;
  /** Fires once the exit (fade-out) animation finishes — the caller's actual removal from its own
   * data/state should happen here, not before, so the element stays visible while it fades. */
  onExited?: () => void;
  /** Flip to `false` to play the exit animation before the element is actually removed from data. */
  visible: boolean;
}

/** Matches SLDS 2's own `--slds-g-duration-md`-scale transitions (~200ms) rather than inventing a
 * separate timing — keeps this fade consistent with the rest of the Cosmos theme's motion. */
const TRANSITION_MS = 180;

const HIDDEN_STYLE: React.CSSProperties = { opacity: 0, transform: 'translateY(-2px)' };
const VISIBLE_STYLE: React.CSSProperties = { opacity: 1, transform: 'none' };

/**
 * Fades a single element in on mount and out before removal — used by `PriceComponentsTable` and
 * `FormulaRowFields` so adding/removing a table row animates instead of popping in/out instantly,
 * and by `FormulaTestingPanel` for the same fade on its (non-table) per-spec input blocks. A
 * plain CSS `transition` on opacity/transform (no animation library) — nothing here is
 * table-specific, it only hands back styles for the caller's own markup.
 */
export const AnimatedItem: React.FC<AnimatedItemProps> = ({ children, onExited, visible }) => {
  const [mounted, setMounted] = useState(false);

  // Starts unmounted so the first paint is the hidden state, then flips true one frame later to
  // actually play the enter (fade-in) transition instead of rendering already visible.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMounted(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  // Calls `onExited` once the fade-out transition has finished.
  useEffect(() => {
    if (mounted && !visible) {
      const timer = window.setTimeout(() => {
        onExited?.();
      }, TRANSITION_MS);
      return () => {
        window.clearTimeout(timer);
      };
    }
    return undefined;
  }, [mounted, onExited, visible]);

  const isShown = mounted && visible;
  return children({
    ...(isShown ? VISIBLE_STYLE : HIDDEN_STYLE),
    transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
  });
};
